/**
 * search.js - Suggestions de recherche en direct.
 *
 * Le formulaire reste fonctionnel sans JavaScript. Ce composant se contente
 * d'interroger /search/suggest et d'injecter le HTML rendu par la section
 * predictive-search, ce qui evite de reformater les prix cote client.
 */

customElements.define(
  'predictive-search',
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.results) return;

      this.controller = null;
      this.timer = null;
      this.cache = new Map();

      this.input.addEventListener('input', () => this.schedule());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));

      this.input.addEventListener('focus', () => {
        if (this.results.innerHTML.trim() !== '') this.results.hidden = false;
      });

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.results.hidden = true;
      });

      this.closest('dialog')?.addEventListener('close', () => this.reset());
    }

    schedule() {
      window.clearTimeout(this.timer);
      // 220 ms : assez court pour paraitre instantane, assez long pour ne pas
      // declencher une requete par frappe.
      this.timer = window.setTimeout(() => this.search(), 220);
    }

    reset() {
      this.results.innerHTML = '';
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.reset();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const links = [...this.results.querySelectorAll('a')];
      if (links.length === 0) return;

      event.preventDefault();
      const current = links.indexOf(document.activeElement);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = (current + step + links.length) % links.length;
      links[next].focus();
    }

    async search() {
      const terms = this.input.value.trim();

      if (terms.length < 2) {
        this.reset();
        return;
      }

      if (this.cache.has(terms)) {
        this.render(this.cache.get(terms));
        return;
      }

      this.controller?.abort();
      this.controller = new AbortController();

      this.results.hidden = false;
      this.results.innerHTML = '<p class="search-results__pending">&nbsp;</p>';
      this.input.setAttribute('aria-expanded', 'true');

      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}search/suggest`, window.location.origin);
      url.searchParams.set('q', terms);
      // lmdh, 22 septembre 2026 : les collections aussi — la page de resultats
      // les affichait deja, la liste deroulante les ignorait.
      url.searchParams.set('resources[type]', 'product,collection,article,page');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('section_id', 'predictive-search');

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));

        const markup = await response.text();
        const html =
          new DOMParser().parseFromString(markup, 'text/html').querySelector('.shopify-section')
            ?.innerHTML ?? '';

        this.cache.set(terms, html);
        this.render(html);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.reset();
      }
    }

    render(html) {
      const empty = html.trim() === '';
      this.results.innerHTML = html;
      this.results.hidden = empty;
      this.input.setAttribute('aria-expanded', String(!empty));
    }
  }
);
